import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useHistory } from 'react-router-dom';

import { Car } from '@/modules/car/model';
import { WARNING_STATUSES } from '@/shared/models/access-status';
import { EntityPage } from '@/shared/models/entity-page';
import { AccessStatus } from '@/shared/view/AccessStatus';
import { IColumnConfig } from '@/shared/view/ListLayout/List';
import { ListLayoutView } from '@/shared/view/ListLayout/ListLayout';
import { MobileListItem } from '@/shared/view/ListLayout/MobileListItem';
import { StatusTag } from '@/shared/view/StatusTag';
import { getStatusTitle } from '@/shared/view/StatusTag/getStatusTitle';
import { Tag } from '@/shared/view/Tag';
import { Icon } from '@src-common/view/components/Icon';

import s from './styles.module.css';

type Props = {
  cars: EntityPage<Car>;
  fetchCars: (_: any, pagination: { offset: number; limit: number }) => void;
  onClickNew: () => void;
  isLoading?: boolean;
};

const getFullModel = (car: Car) => [car.make, car.model].join(' ');

const columns: IColumnConfig<Car>[] = [
  {
    style: {
      width: '25%',
    },
    renderHeader: () => (
      <FormattedMessage defaultMessage="Model" id="MuMIc6" description="Cars: column header" />
    ),
    renderCell: getFullModel,
  },
  {
    style: {
      width: '15%',
    },
    renderHeader: () => (
      <FormattedMessage
        defaultMessage="License plate"
        id="dnFIhm"
        description="Cars: column header"
      />
    ),
    renderCell: car => car.licensePlate,
  },
  {
    renderHeader: () => (
      <FormattedMessage defaultMessage="Tags" id="rTlW3o" description="Tags: column header" />
    ),
    renderCell: car => (
      <div className={s.tags}>
        {car.tags?.map(tag => (
          <Tag key={tag}>{tag}</Tag>
        ))}
      </div>
    ),
  },
  {
    style: {
      width: '80px',
    },
    renderHeader: () => (
      <FormattedMessage defaultMessage="Status" id="aRouek" description="Tags: column header" />
    ),
    renderCell: car => (
      <div className={s.status}>
        <StatusTag status={car.onlineStatus} />
        <AccessStatus status={car.accessStatus} />
      </div>
    ),
  },
];

const hasWarning = (car: Car) => WARNING_STATUSES.includes(car.accessStatus);

const GROUP_ORDER = ['online', 'busy', 'offline'];

const View = ({ cars, fetchCars, onClickNew, isLoading = false }: Props) => {
  const history = useHistory();

  const onClickRow = useCallback(
    (car: Car | undefined) => car && history.push(`/vehicles/${car.id}`),
    [history],
  );

  return (
    <ListLayoutView<Car, ''>
      wrapperClassName={s.wrapper}
      title={<FormattedMessage defaultMessage="Vehicles" id="MiPb62" description="Page title" />}
      data={cars.items}
      total={cars.total}
      limit={cars.limit}
      columns={columns}
      getEntityId={car => String(car.id)}
      onFetchData={fetchCars}
      shouldBeEmptyWithoutFilters={false}
      getEntityGroup={car => car.onlineStatus}
      groupOrder={GROUP_ORDER}
      translateGroupName={getStatusTitle}
      renderItem={car => (
        <Link to={`/vehicles/${car.id}`}>
          <MobileListItem
            title={getFullModel(car)}
            note={car.licensePlate}
            right={
              hasWarning(car) ? (
                <Icon icon={'warning'} className={s.red} size={'22px'} />
              ) : (
                <Icon icon={'chevron-to-end'} />
              )
            }
          />
        </Link>
      )}
      onClickRow={onClickRow}
      action={useMemo(
        () => ({
          onClick: onClickNew,
          title: (
            <FormattedMessage
              defaultMessage="Add new"
              id="m9K3yy"
              description="Create car button"
            />
          ),
        }),
        [onClickNew],
      )}
      isLoading={isLoading}
    />
  );
};

export { View };
